import { useState, useEffect, useCallback } from 'react';
import { getAllProgress } from '../../lib/db';
import type { LessonProgress } from '../../lib/types';
import { computerLessons } from './data';
import ComputerLessonView from './ComputerLessonView';

export default function ComputerModule() {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());

  const loadCompleted = useCallback(async () => {
    try {
      const all: LessonProgress[] = await getAllProgress();
      const ids = new Set(
        all.filter((p) => p.moduleId === 'computer').map((p) => p.lessonId),
      );
      setCompletedIds(ids);
    } catch {
      // Offline or empty — keep set empty
    }
  }, []);

  useEffect(() => {
    loadCompleted();
  }, [loadCompleted]);

  const completedCount = computerLessons.filter((l) =>
    completedIds.has(l.id),
  ).length;
  const totalCount = computerLessons.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  /* ─── Lesson detail view ─── */
  if (selectedIdx !== null) {
    const lesson = computerLessons[selectedIdx];
    return (
      <ComputerLessonView
        lesson={lesson}
        onBack={() => {
          setSelectedIdx(null);
          loadCompleted();
        }}
        onComplete={() => {
          // Move to next lesson or go back to list
          if (selectedIdx + 1 < totalCount) {
            setSelectedIdx(selectedIdx + 1);
            loadCompleted();
          } else {
            setSelectedIdx(null);
            loadCompleted();
          }
        }}
      />
    );
  }

  /* ─── Lesson list view ─── */
  return (
    <div className="cm-page page">
      <h1 className="page-title">مهارت\u200Cهای کامپیوتر</h1>

      {/* Progress summary */}
      <div className="cm-progress-summary card">
        <div className="cm-progress-text">
          <span>{completedCount} از {totalCount} درس تکمیل شده</span>
          <span className="cm-progress-percent">{progressPercent}٪</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Lesson cards */}
      <ul className="cm-lesson-list">
        {computerLessons.map((lesson, idx) => {
          const done = completedIds.has(lesson.id);
          return (
            <li key={lesson.id} className="cm-lesson-item">
              <button
                className={`cm-lesson-card card ${done ? 'cm-lesson-done' : ''}`}
                onClick={() => setSelectedIdx(idx)}
              >
                <span className="cm-lesson-number">{idx + 1}</span>

                <span className="cm-lesson-title">{lesson.title}</span>

                {done && (
                  <span className="cm-lesson-check" aria-label="تکمیل شده">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" fill="rgba(52,168,83,0.12)" />
                      <path d="M8 12l2.5 2.5L16 9" />
                    </svg>
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
